import { useState, useContext } from "react";
import Joyride from "react-joyride";
import { useUsageAndBillingTour } from "../hooks/useUsageAndBillingTour";
import { usageAndBillingTourSteps } from "../tours";
import {
  Typography,
  Box,
  Grid,
  Tooltip,
  IconButton,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { useAlert } from "../functions/useAlert";
import UsersTable from "../components/dashboard/UsersTable";
import FeatureUsageCard from "../components/dashboard/FeatureUsageCard";
import LiveActivityCard from "../components/dashboard/LiveActivityCard";
import ExperimentalFeatureUsageCard from "../components/dashboard/ExperimentalFeatureUsageCard";
import AgentUsageLogCard from "../components/dashboard/AgentUsageLogCard";
import UserSessionDetailsModal from "../components/dashboard/UserSessionDetailsModal";
import GlobalFilterBar from "../components/dashboard/GlobalFilterBar";
import TotalLoginsAndHours from "../components/dashboard/TotalLoginsAndHours";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { DataContext } from "../contexts/DataProvider";
import { FilterContext } from "./FilterContext";

const UsageAndBilling = ({ isAdmin }: { isAdmin: boolean }) => {
  const [setAlert] = useAlert();
  const { login } = useContext(DataContext);

  // FilterContext state
  const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(14, "day"),
    dayjs(),
  ]);

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUserName, setSelectedUserName] = useState<string | undefined>(
    undefined,
  );
  const [modalOpen, setModalOpen] = useState(false);

  // --- React Joyride State ---
  const { runTour, setRunTour, handleJoyrideCallback } = useUsageAndBillingTour();
  // --- End React Joyride ---

  const handleRowClick = (userId: string, userName?: string) => {
    setSelectedUserId(userId);
    setSelectedUserName(userName);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedUserId(null);
    setSelectedUserName(undefined);
  };

  if (!isAdmin) {
    return <div>Only Admin can access!</div>;
  }

  return (
    <FilterContext.Provider
      value={{
        selectedOrgIds,
        selectedUserIds,
        dateRange,
        setSelectedOrgIds,
        setSelectedUserIds,
        setDateRange,
      }}
    >
      <Box>
        <Joyride
          steps={usageAndBillingTourSteps}
          run={runTour}
          continuous
          showProgress
          showSkipButton
          disableScrolling={true}
          disableScrollParentFix={true}
          floaterProps={{
            disableAnimation: false,
            styles: {
              floater: {
                filter: "none",
              },
            },
          }}
          callback={handleJoyrideCallback}
          styles={{
            options: {
              zIndex: 10000,
              primaryColor: "#1976d2",
            },
            tooltip: {
              borderRadius: "12px",
              padding: "16px 20px",
            },
            buttonNext: {
              backgroundColor: "#1976d2",
              borderRadius: "8px",
            },
            buttonBack: {
              color: "#555",
            },
          }}
        />
        <Box sx={{ minHeight: "100vh" }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="h5" fontWeight="bold">
                User Activities
              </Typography>
            </Box>
            <Box display="flex" gap={2} alignItems="center">
              {/* Tour Button */}
              <Tooltip title="Start Guided Tour">
                <IconButton
                  id="tour-restart-button-admin"
                  onClick={() => setRunTour(true)}
                >
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <GlobalFilterBar />
            <Grid container spacing={3}>
              <Grid item xs={12} id="tour-live-activity-card">
                <LiveActivityCard />
              </Grid>
              <Grid item xs={12}>
                <TotalLoginsAndHours />
              </Grid>
              <Grid item xs={12} id="tour-users-table">
                <UsersTable onRowClick={(userId, userName) => handleRowClick(userId, userName)} />
              </Grid>
              <Grid item xs={12} id="tour-feature-usage-card">
                <FeatureUsageCard />
              </Grid>
              <Grid item xs={12}>
                <ExperimentalFeatureUsageCard />
              </Grid>
              <Grid item xs={12}>
                <AgentUsageLogCard />
              </Grid>
            </Grid>
            <UserSessionDetailsModal
              open={modalOpen}
              onClose={handleModalClose}
              userId={selectedUserId}
              userName={selectedUserName}
              orgId={selectedOrgIds.length === 1 ? selectedOrgIds[0] : undefined}
            />
          </LocalizationProvider>
        </Box>
      </Box>
    </FilterContext.Provider>
  );
};

export default UsageAndBilling;
